import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Linkedin, Calendar, MapPin, Clock } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    message: '',
  });

  // TOGGLE: Email Address Display - Shows/hides email address on hover for anti-spam protection
  // Current State: false - Email is hidden until hover
  // How to Toggle: Controlled by hover events (automatic)
  // Category: Automatic
  const [showEmail, setShowEmail] = useState(false);
  
  // TOGGLE: Contact Form - Hides/shows the contact form section
  // Current State: false - Form is hidden from users
  // How to Toggle: Change useState(false) to useState(true)
  // Category: Development
  const [showContactForm, setShowContactForm] = useState(false);

  // Email obfuscation function
  const getObfuscatedEmail = () => {
    // Using character codes to make it harder for bots to parse
    const parts = [
      String.fromCharCode(104, 101, 108, 108, 111), // 'hello'
      String.fromCharCode(64), // '@'
      String.fromCharCode(104, 121, 112, 101, 114, 104, 117, 109, 97, 110, 101), // 'hyperhumane'
      String.fromCharCode(46), // '.'
      String.fromCharCode(99, 111, 109) // 'com'
    ];
    return parts.join('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission - would integrate with backend service
    console.log('Form submitted:', formData);
    alert('Thank you for your message. I\'ll get back to you within 24 hours.');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleEmailClick = () => {
    const email = getObfuscatedEmail();
    window.location.href = `mailto:${email}`;
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email',
      description: showEmail ? getObfuscatedEmail() : 'Hover to reveal email address',
      action: handleEmailClick,
      color: 'from-primary-100 to-accent-100',
      iconColor: 'text-primary-700',
      isEmail: true
    },
    {
      icon: Linkedin,
      title: 'LinkedIn',
      description: 'Connect for professional networking',
      link: 'https://www.linkedin.com/in/neilsinghmamak/',
      color: 'from-primary-100 to-accent-100',
      iconColor: 'text-primary-700'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="section bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h1 className="section-title">Contact Neil Singh Mamak</h1>
            <p className="section-subtitle">
              Let's discuss how I can help drive your organization's strategic transformation and measurable results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="section bg-white pt-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {contactMethods.map((method, index) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card flex items-center space-x-6 group"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${method.color} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                  <method.icon size={32} className={method.iconColor} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-primary-900 mb-2">
                    {method.title}
                  </h3>
                  <p className="text-primary-800 mb-4">
                    {method.description}
                  </p>
                  {method.isEmail ? (
                    <button
                      onClick={method.action}
                      onMouseEnter={() => setShowEmail(true)}
                      onMouseLeave={() => setShowEmail(false)}
                      className="btn-primary"
                    >
                      Send Message
                    </button>
                  ) : (
                    <a
                      href={method.link}
                      target={method.link.startsWith('http') ? '_blank' : undefined}
                      rel={method.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="btn-primary"
                    >
                      {method.title === 'LinkedIn' ? 'Connect' : 'Send Message'}
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Optional: Toggle button for development/testing */}
      {/* Uncomment the line below to add a toggle button */}
      {/* <button onClick={() => setShowContactForm(!showContactForm)} className="fixed top-4 right-4 bg-primary-500 text-white px-4 py-2 rounded z-50">Toggle Form</button> */}

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="flex justify-center items-center py-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-primary-400 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>

      {/* Ready to Drive Strategic Transformation card */}
      <section className="section bg-white">
        <div className="flex justify-center">
          <div className="bg-white rounded-xl p-8 border border-primary-100 max-w-4xl mx-auto w-full">
            <h3 className="text-xl font-semibold text-primary-900 mb-4">
              Ready to Drive Strategic Transformation?
            </h3>
            <p className="text-primary-700 leading-relaxed mb-6">
              I'm currently focused on roles that leverage my strategic leadership experience to support C-Suite executives 
              and drive organizational transformation. My background in AI strategy, product management, and business development 
              positions me uniquely to bridge executive vision with operational excellence.
            </p>
            <div className="flex items-center justify-center gap-2 text-primary-800">
              <MapPin size={20} />
              <span className="font-medium">Based in San Francisco</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form - Hidden for future use */}
      {showContactForm && (
        <section className="section bg-white">

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h2 className="section-title">Send a Message</h2>
            <p className="section-subtitle">
              Tell me about your organization and how I can help
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-primary-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-primary-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="your.email@company.com"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-primary-700 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Your organization"
                />
              </div>
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-primary-700 mb-2">
                  Your Role
                </label>
                <input
                  type="text"
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Your position"
                />
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-primary-700 mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                placeholder="Tell me about your organization, the role you're looking to fill, and how I can contribute to your strategic objectives..."
              />
            </div>

            <div className="text-center">
              <button
                type="submit"
                className="btn-primary px-8 py-3 text-lg"
              >
                Send Message
              </button>
            </div>
          </motion.form>
        </div>
      </section>
      )}

      <section className="section bg-white">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h2 className="section-title">Location</h2>
            <p className="section-subtitle">
              <strong>Location:</strong> San Francisco, CA
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 